/**
 * External dependencies
 */
import clsx from 'clsx';
import removeAccents from 'remove-accents';

/**
 * WordPress dependencies
 */
import {
	RichText,
	useBlockProps,
	__experimentalGetBorderClassesAndStyles as getBorderClassesAndStyles,
	__experimentalGetColorClassesAndStyles as getColorClassesAndStyles,
	__experimentalGetSpacingClassesAndStyles as getSpacingClassesAndStyles,
} from '@wordpress/block-editor';
import { __unstableStripHTML as stripHTML } from '@wordpress/dom';

/**
 * Get the name attribute from a content string.
 *
 * @param {string} content The block content.
 *
 * @return {string} Returns the slug.
 */
export const getNameFromLabel = ( content ) => {
	return (
		removeAccents( stripHTML( content ) )
			// Convert anything that's not a letter or number to a hyphen.
			.replace( /[^\p{L}\p{N}]+/gu, '-' )
			// Convert to lowercase
			.toLowerCase()
			// Remove any remaining leading or trailing hyphens.
			.replace( /(^-+)|(-+$)/g, '' )
	);
};

export default function save( { attributes, className } ) {
	const { type, name, label, required, placeholder, value, hidden, id } =
		attributes;

	const borderProps = getBorderClassesAndStyles( attributes );
	const colorProps = getColorClassesAndStyles( attributes );
	const spacingProps = getSpacingClassesAndStyles( attributes );

	const inputStyle = {
		...borderProps.style,
		...colorProps.style,
		...spacingProps.style,
	};

	const inputClasses = clsx(
		colorProps.className,
		borderProps.className,
		spacingProps.className
	);
	const TagName = 'textarea' === type ? 'textarea' : 'input';

	const blockProps = useBlockProps.save( {
		className: clsx( className, {
			'is-label-inline': 'checkbox' === type,
		} ),
	} );

	if ( 'hidden' === type ) {
		return <input type={ type } name={ name } value={ value } />;
	}

	return (
		<div { ...blockProps }>
			<label hidden={ hidden } htmlFor={ id }>
				<span>
					<RichText.Content value={ label } />
				</span>
				{ required && <span className="required">*</span> }
			</label>
			<TagName
				className={ inputClasses }
				type={ type ? type : 'textarea' }
				name={ name || getNameFromLabel( label ) }
				required={ required }
				aria-required={ required }
				placeholder={ placeholder || undefined }
				style={ inputStyle }
				id={ id }
			/>
		</div>
	);
}
